import { isValidArray } from '@/libs/utils/array'

/**
 * Checks if an object has the specified properties
 * @param object - The object to be checked
 * @param propertyNames - Property names to check for
 * @returns True if the object has all specified properties
 */
const checkHasPropertiesInObject = (
  object: Record<string, any> | null | undefined,
  ...propertyNames: string[]
): boolean => {
  if (object === null) {
    return false
  }
  if (object === undefined) {
    return false
  }

  if (isValidArray(propertyNames, 1)) {
    return propertyNames.every((propertyName) => Object.hasOwn(object, propertyName))
  }

  return Object.hasOwn(object, propertyNames[0])
}

/**
 * Checks if a value is a plain object
 * @param object - The value to check
 * @returns True if the value is a plain object
 */
const isObjectChecker = (object: unknown): object is Record<string, any> => {
  return !!(
    object
    && typeof object === 'object'
    && Object.getPrototypeOf(object) === Object.prototype
  )
}

/**
 * Checks if an object is empty
 * @param object - The object to check
 * @returns True if the object is empty or not a plain object
 */
const isEmptyObject = (object: unknown): boolean => {
  if (isObjectChecker(object)) {
    return (
      Object.keys(object).length === 0
      && Object.getPrototypeOf(object) === Object.prototype
    )
  }
  return true
}

/**
 * Compares two objects for shallow equality
 * @param object1 - First object to compare
 * @param object2 - Second object to compare
 * @returns True if the objects have identical keys with identical values
 */
const compareObjects = (
  object1: Record<string, any>,
  object2: Record<string, any>,
): boolean => {
  const keys1 = Object.keys(object1)
  const keys2 = Object.keys(object2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    if (object1[key] !== object2[key]) {
      return false
    }
  }

  return true
}

/**
 * Removes a key from an object
 * @param key - Key to remove
 * @param object - Object to remove the key from
 * @returns New object without the specified key
 */
const removeKey = <T extends Record<string, any>>(
  key = '',
  object: T = {} as T,
): Record<string, any> => {
  if (!object || typeof object !== 'object') return {} as T

  const { [key]: _, ...rest } = object
  return rest
}

/**
 * Removes multiple keys from an object
 * @param keys - Array of keys to remove
 * @param object - Object to remove keys from
 * @returns New object without the specified keys
 */
const removeKeys = <T extends Record<string, any>>(
  keys: string[] = [],
  object: T = {} as T,
): Record<string, any> => keys.reduce((r, k) => removeKey(k as any, r), object)

/**
 * Compares if two objects are deeply equal
 * @param object1 - First object to compare
 * @param object2 - Second object to compare
 * @returns True if the objects are deeply equal
 */
const isEqual = <T extends Record<string, any>>(
  object1: T = {} as T,
  object2: T = {} as T,
): boolean => {
  return JSON.stringify(object1) === JSON.stringify(object2)
}

/**
 * Checks if all properties in an object have valid values
 * @param object - Object to check
 * @returns True if all properties have valid values
 */
const hasAllPropertiesValid = (object: Record<string, any>): boolean => {
  if (isEmptyObject(object)) {
    return false
  }

  const hasInvalidProperties = Object.values(object).some((value) => {
    if (typeof value === 'number' || typeof value === 'boolean') {
      return false
    }
    if (typeof value === 'object' && !!value) {
      if (Array.isArray(value)) {
        return value.length === 0
      }
      return isEmptyObject(value)
    }
    return !value
  })
  return !hasInvalidProperties
}

export {
  checkHasPropertiesInObject,
  compareObjects,
  hasAllPropertiesValid,
  isEmptyObject,
  isEqual,
  isObjectChecker,
  removeKey,
  removeKeys,
}
